import { useState } from 'react';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6bvt1atehdqnvnpi6c0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Contact', path: '/contact' }
  ];

  return (
    <>
      <SEO
        title="Contact Us - La Antigua Guatemala"
        description="Get in touch with La Antigua Guatemala travel team. Questions about your trip, suggestions, corrections or partnership inquiries welcome."
        keywords="contact antigua guatemala, travel questions, antigua travel help, trip planning assistance"
        canonical="https://la-antigua-guatemala.com/contact"
        ogImage={{
          url: "https://public.readdy.ai/ai/img_res/9fd6bdb2502e9ed52c91711b80a977b1.jpg",
          width: 1200,
          height: 630
        }}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-6">
            <Breadcrumbs items={breadcrumbs} />
            <h1 className="text-5xl font-bold mt-8 mb-4">Contact Us</h1>
            <p className="text-xl text-slate-200 max-w-3xl">
              Have questions about traveling to Antigua Guatemala? Need help planning your trip? We're here to help.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible. We typically respond within 24-48 hours.
              </p>

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-slate-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select a subject</option>
                    <option value="Trip Planning Question">Trip Planning Question</option>
                    <option value="Content Correction">Content Correction</option>
                    <option value="Suggestion">Suggestion</option>
                    <option value="Partnership Inquiry">Partnership Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                    Message * <span className="text-xs text-slate-500">(max 500 characters)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <div className="text-xs text-slate-500 mt-1 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all disabled:bg-slate-400 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-xl"></i>
                      <span className="font-semibold">Message sent successfully!</span>
                    </div>
                    <p className="text-sm mt-1">We'll get back to you within 24-48 hours.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-fill text-xl"></i>
                      <span className="font-semibold">Something went wrong.</span>
                    </div>
                    <p className="text-sm mt-1">Please check your information and try again.</p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Contact Us?</h2>
              
              <div className="space-y-6">
                {/* Trip Planning */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">Trip Planning Questions</h3>
                      <p className="text-slate-600 text-sm">
                        Need help planning your Antigua itinerary? Questions about hotels, transportation, or activities? We're happy to help.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Corrections */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg flex-shrink-0">
                      <i className="ri-edit-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">Content Corrections</h3>
                      <p className="text-slate-600 text-sm">
                        Found outdated information or an error? Let us know so we can keep our guides accurate and up-to-date.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Suggestions */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                      <i className="ri-lightbulb-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">Suggestions</h3>
                      <p className="text-slate-600 text-sm">
                        Have ideas for new guides or topics we should cover? We'd love to hear your suggestions.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Partnerships */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-purple-100 text-purple-600 rounded-lg flex-shrink-0">
                      <i className="ri-handshake-line text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">Partnership Inquiries</h3>
                      <p className="text-slate-600 text-sm">
                        Interested in partnering with us? Reach out to discuss collaboration opportunities.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="mt-8 bg-teal-50 border border-teal-200 p-6 rounded-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Response Time</h3>
                    <p className="text-sm text-slate-700">
                      We typically respond to all inquiries within 24-48 hours. For urgent travel questions, please mention that in your message.
                    </p>
                  </div>
                </div>
              </div>

              {/* Alternative Contact */}
              <div className="mt-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-3">Other Ways to Connect</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Looking for quick answers? Check out our comprehensive guides or use our trip planning tool.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/antigua-guatemala-travel-guide"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-all text-sm whitespace-nowrap"
                  >
                    <i className="ri-book-open-line"></i>
                    Read Travel Guide
                  </a>
                  <a
                    href="/plan-my-antigua-trip"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-all text-sm whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    Plan My Trip
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-slate-50 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Before You Contact Us</h2>
            <p className="text-slate-600 text-center mb-12">
              Your question might already be answered in our guides. Here are some common topics:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <a href="/antigua-guatemala-travel-guide" className="bg-white p-6 rounded-lg border border-slate-200 hover:border-teal-500 hover:shadow-md transition-all group">
                <h3 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                  General Travel Information
                </h3>
                <p className="text-sm text-slate-600">
                  Everything you need to know about visiting Antigua Guatemala
                </p>
              </a>

              <a href="/itineraries" className="bg-white p-6 rounded-lg border border-slate-200 hover:border-teal-500 hover:shadow-md transition-all group">
                <h3 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Itinerary Planning
                </h3>
                <p className="text-sm text-slate-600">
                  Ready-made travel plans for 2, 3, 5 or 7 days
                </p>
              </a>

              <a href="/best-hotels-in-antigua-guatemala" className="bg-white p-6 rounded-lg border border-slate-200 hover:border-teal-500 hover:shadow-md transition-all group">
                <h3 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Where to Stay
                </h3>
                <p className="text-sm text-slate-600">
                  Hotel recommendations for every budget and travel style
                </p>
              </a>

              <a href="/is-antigua-guatemala-safe" className="bg-white p-6 rounded-lg border border-slate-200 hover:border-teal-500 hover:shadow-md transition-all group">
                <h3 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Safety Information
                </h3>
                <p className="text-sm text-slate-600">
                  Complete safety guide for travelers to Antigua
                </p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
